// attachment-input.js — single funnel for paste, drag-drop, and the /attach
// slash command.
//
// Routing every attachment trigger through one entry point keeps size limits,
// image transcoding, sanitization, and autosave consistent across surfaces.
// The DOM listeners (paste on the editor, drop on editor/preview) stay in
// app-init — they're boot wiring; this leaf owns the funnel itself.
//
// Relocated out of app-init (a non-exporting boot script) so the attachment
// picker and the boot listeners both import a real ESM home. The
// window.AttachmentInput shim is kept assigned for the picker's runtime read.

import { getAutoSaveTimer, getCurrentFileName } from '../core/app-state.js';
import { AutoEditQueue } from '../editor/auto-edit-queue.js';
import { insertAtCursor } from '../editor/editor-commands.js';
import { updateStatus } from '../ui/status-pill.js';
import { arrayBufferToBase64 } from '../utils/binary.js';
import { sanitizeAttachmentName, uniqueAttachmentName } from './attachment-helpers.js';
import { maybeTranscodeImage } from './image-transcode.js';
import { autoSaveNote } from './note-manager.js';

// Hard cap on a single attachment. Large pastes would otherwise spend several
// seconds in arrayBuffer() + base64 + IDB write before tripping a quota
// failure deep in storage; refuse up front with a clear message instead.
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/**
 * @param {any} file - a File/Blob to attach to the current note.
 * @returns {Promise<boolean>} true on success.
 */
export async function attachFileToCurrentNote(file: any) {
  if (!file) return false;
  if (!getCurrentFileName()) {
    updateStatus('Open or create a note before attaching files.', false);
    return false;
  }
  // HEIC / oversized photos transcode to a portable JPEG before the size
  // check so the 25 MB ceiling reflects what we actually store.
  let processed = file;
  if (file.type?.startsWith('image/')) {
    try {
      processed = await maybeTranscodeImage(file);
    } catch (e: any) {
      console.warn('[attach] transcode failed, using original:', e);
    }
  }
  if (typeof processed.size === 'number' && processed.size > MAX_ATTACHMENT_BYTES) {
    updateStatus('File too large (max 25 MB).', false);
    return false;
  }
  const rawName = processed.name || `attachment_${Date.now()}`;
  const dotIdx = rawName.lastIndexOf('.');
  const ext = dotIdx >= 0 ? rawName.slice(dotIdx + 1).toLowerCase() : '';
  const base = dotIdx >= 0 ? rawName.slice(0, dotIdx) : rawName;
  const noteName = getCurrentFileName();
  let safeFilename = sanitizeAttachmentName(base) + (ext ? `.${ext}` : '');
  // Disambiguate against attachments already on this note. sanitizeAttachmentName
  // collapses whitespace/special chars, so two files (e.g. "photo 1.jpg" and
  // "photo_1.jpg", or the same "image.png" pasted twice) can sanitize to the
  // same name and silently overwrite each other. Append _2, _3, … instead.
  try {
    const existing = await window.NoteStorage.listAttachments(noteName);
    safeFilename = uniqueAttachmentName(new Set(existing), safeFilename);
  } catch (e: any) {
    console.warn('[attach] attachment collision check failed, using sanitized name:', e);
  }

  updateStatus(`Attaching ${safeFilename}…`, true);
  try {
    const base64 = arrayBufferToBase64(await processed.arrayBuffer());
    const saved = await window.NoteStorage.writeAttachment(noteName, safeFilename, base64);
    if (!saved) {
      updateStatus('Failed to save attachment.', false);
      return false;
    }
    const isImage = (processed.type || '').startsWith('image/');
    const md = isImage
      ? `![${safeFilename}](attachment:${safeFilename})`
      : `[${safeFilename}](attachment:${safeFilename})`;
    insertAtCursor(md);
    updateStatus(`Attached ${safeFilename}.`, true);
    clearTimeout(getAutoSaveTimer());
    if (AutoEditQueue) await AutoEditQueue.flush({ reason: 'attach' });
    await autoSaveNote();
    return true;
  } catch (err: any) {
    console.error('Attachment error:', err);
    updateStatus('Failed to attach file.', false);
    return false;
  }
}

// Back-compat shim: notes/attachment-picker.js reads window.AttachmentInput
// at runtime. Keep it assigned from the ESM home.
if (typeof window !== 'undefined') {
  window.AttachmentInput = {
    attachFile: attachFileToCurrentNote,
    MAX_ATTACHMENT_BYTES,
  };
}
